import type {
  FeatureDto,
  BuildDto,
  PipelineDetailDto,
  PipelineListItemDto,
  UploadDto,
  UploadTokenDto,
  ExecutionRunDto,
  OrganisationDto,
  ProjectDto,
  ReleaseDetailDto,
  ReleaseListItemDto,
  ResultsFileDto,
  SuiteDetailDto,
  SuiteListItemDto,
  SuiteExecutionDto,
  TestDetailDto,
  TestListItemDto,
  TestExecutionDto,
  ProjectCsvMappingDto,
} from '@flowqa/shared';
import type { QueryResult } from '@flowqa/shared/query';

type FeatureDtoWire = Omit<FeatureDto, 'plannedFor' | 'releasedAt' | 'deletedAt'> & {
  plannedFor?: Date | string | null;
  releasedAt?: Date | string | null;
  deletedAt: Date | string | null;
};

type ReleaseListItemDtoWire = Omit<
  ReleaseListItemDto,
  'plannedFor' | 'releasedAt' | 'deletedAt'
> & {
  plannedFor: Date | string | null;
  releasedAt: Date | string | null;
  deletedAt: Date | string | null;
};

type ReleaseDetailDtoWire = Omit<ReleaseDetailDto, 'plannedFor' | 'releasedAt' | 'deletedAt'> & {
  plannedFor: Date | string | null;
  releasedAt: Date | string | null;
  deletedAt: Date | string | null;
};

type SuiteExecutionDtoWire = Omit<SuiteExecutionDto, 'timestamp' | 'createdAt' | 'deletedAt'> & {
  timestamp: Date | string;
  createdAt: Date | string;
  deletedAt: Date | string | null;
};

type ExecutionRunDtoWire = Omit<
  ExecutionRunDto,
  'startedAt' | 'finishedAt' | 'resultsFileCreatedAt' | 'deletedAt'
> & {
  startedAt: Date | string | null;
  finishedAt: Date | string | null;
  resultsFileCreatedAt?: Date | string | null;
  deletedAt: Date | string | null;
};

type ProjectCsvMappingDtoWire = Omit<ProjectCsvMappingDto, 'createdAt' | 'updatedAt'> & {
  createdAt: Date | string;
  updatedAt: Date | string;
};

type BuildDtoWire = Omit<BuildDto, 'createdAt' | 'startedAt' | 'finishedAt' | 'deletedAt'> & {
  createdAt: Date | string;
  startedAt: Date | string | null;
  finishedAt: Date | string | null;
  deletedAt: Date | string | null;
};

const coerceDate = (value: Date | string | null | undefined): Date | null => {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.valueOf()) ? null : parsed;
};

const coerceRequiredDate = (value: Date | string): Date => {
  if (value instanceof Date) return value;
  return new Date(value);
};

export const mapQueryResult = <TInput, TOutput>(
  result: QueryResult<TInput>,
  mapItem: (item: TInput) => TOutput,
): QueryResult<TOutput> => ({ ...result, items: result.items.map(mapItem) });

export const mapOrganisationDto = (dto: OrganisationDto): OrganisationDto => ({
  ...dto,
});

export const mapProjectDto = (dto: ProjectDto): ProjectDto => ({
  ...dto,
  deletedAt: coerceDate(dto.deletedAt),
  projectApiKeyCreatedAt: coerceDate(dto.projectApiKeyCreatedAt),
  projectApiKeyLastUsedAt: coerceDate(dto.projectApiKeyLastUsedAt),
});

export const mapProjectCsvMappingDto = (dto: ProjectCsvMappingDtoWire): ProjectCsvMappingDto => ({
  ...dto,
  createdAt: coerceRequiredDate(dto.createdAt),
  updatedAt: coerceRequiredDate(dto.updatedAt),
});

export const mapExecutionRunDto = (dto: ExecutionRunDtoWire): ExecutionRunDto => ({
  ...dto,
  startedAt: coerceDate(dto.startedAt),
  finishedAt: coerceDate(dto.finishedAt),
  resultsFileCreatedAt: coerceDate(dto.resultsFileCreatedAt),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapFeatureDto = (dto: FeatureDtoWire): FeatureDto => ({
  ...dto,
  plannedFor: coerceDate(dto.plannedFor),
  releasedAt: coerceDate(dto.releasedAt),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapReleaseListItemDto = (dto: ReleaseListItemDtoWire): ReleaseListItemDto => ({
  ...dto,
  plannedFor: coerceDate(dto.plannedFor),
  releasedAt: coerceDate(dto.releasedAt),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapReleaseDetailDto = (dto: ReleaseDetailDtoWire): ReleaseDetailDto => ({
  ...dto,
  plannedFor: coerceDate(dto.plannedFor),
  releasedAt: coerceDate(dto.releasedAt),
  deletedAt: coerceDate(dto.deletedAt),
});

const coerceSuiteLatestExecution = (
  latestExecution: SuiteListItemDto['latestExecution'],
): SuiteListItemDto['latestExecution'] => {
  if (!latestExecution) return latestExecution;
  return {
    ...latestExecution,
    timestamp: coerceRequiredDate(latestExecution.timestamp),
  };
};

export const mapSuiteListItemDto = (dto: SuiteListItemDto): SuiteListItemDto => ({
  ...dto,
  latestExecution: coerceSuiteLatestExecution(dto.latestExecution),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapSuiteDetailDto = (dto: SuiteDetailDto): SuiteDetailDto => ({
  ...dto,
  latestExecution: coerceSuiteLatestExecution(dto.latestExecution),
  deletedAt: coerceDate(dto.deletedAt),
});

const coerceTestLatestExecution = (
  latestExecution: TestListItemDto['latestExecution'],
): TestListItemDto['latestExecution'] => {
  if (!latestExecution) return latestExecution;
  return {
    ...latestExecution,
    timestamp: coerceRequiredDate(latestExecution.timestamp),
  };
};

export const mapTestListItemDto = (dto: TestListItemDto): TestListItemDto => ({
  ...dto,
  latestExecution: coerceTestLatestExecution(dto.latestExecution),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapTestDetailDto = (dto: TestDetailDto): TestDetailDto => ({
  ...dto,
  latestExecution: coerceTestLatestExecution(dto.latestExecution),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapSuiteExecutionDto = (dto: SuiteExecutionDtoWire): SuiteExecutionDto => ({
  ...dto,
  timestamp: coerceRequiredDate(dto.timestamp),
  createdAt: coerceRequiredDate(dto.createdAt),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapTestExecutionDto = (dto: TestExecutionDto): TestExecutionDto => ({
  ...dto,
  timestamp: coerceRequiredDate(dto.timestamp),
  createdAt: coerceRequiredDate(dto.createdAt),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapBuildDto = (dto: BuildDtoWire): BuildDto => ({
  ...dto,
  createdAt: coerceRequiredDate(dto.createdAt),
  startedAt: coerceDate(dto.startedAt),
  finishedAt: coerceDate(dto.finishedAt),
  deletedAt: coerceDate(dto.deletedAt),
});

const coercePipelineLatestBuild = (
  latestBuild: PipelineListItemDto['latestBuild'],
): PipelineListItemDto['latestBuild'] => {
  if (!latestBuild) return latestBuild;
  return {
    ...latestBuild,
    timestamp: coerceRequiredDate(latestBuild.timestamp),
  };
};

export const mapPipelineListItemDto = (dto: PipelineListItemDto): PipelineListItemDto => ({
  ...dto,
  latestBuild: coercePipelineLatestBuild(dto.latestBuild),
  recentBuilds: dto.recentBuilds,
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapPipelineDetailDto = (dto: PipelineDetailDto): PipelineDetailDto => ({
  ...dto,
  latestBuild: coercePipelineLatestBuild(dto.latestBuild),
  deletedAt: coerceDate(dto.deletedAt),
});

export const mapUploadDto = (dto: UploadDto): UploadDto => ({
  ...dto,
  createdAt: coerceRequiredDate(dto.createdAt),
});

export const mapUploadTokenDto = (dto: UploadTokenDto): UploadTokenDto => ({
  ...dto,
  createdAt: coerceDate(dto.createdAt),
  lastUsedAt: coerceDate(dto.lastUsedAt),
  expiresAt: coerceDate(dto.expiresAt),
  revokedAt: coerceDate(dto.revokedAt),
});

export const mapResultsFileDto = (dto: ResultsFileDto): ResultsFileDto => ({
  ...dto,
  timestamp: coerceDate(dto.timestamp),
  createdAt: coerceRequiredDate(dto.createdAt),
  updatedAt: coerceRequiredDate(dto.updatedAt),
});
